import React from "react";
import EditLostItemModal from "@/components/lost-found/EditLostItemModal";
import LostItemSignatureModal from "@/components/lost-found/LostItemSignatureModal";
import LostItemTimelineModal from "@/components/lost-found/LostItemTimelineModal";
import LostItemInfoModal from "@/components/lost-found/LostItemInfoModal";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { LostItem } from "@/types";
import { Button } from "@/components/ui/button";
import { useUpdateLostItem } from '@/hooks/useLostItems';

interface DialogsProps {
  editItemOpen: boolean;
  setEditItemOpen: (open: boolean) => void;
  selectedItem: LostItem | null;
  signatureModalOpen: boolean;
  setSignatureModalOpen: (open: boolean) => void;
  handleConfirmSignature: (receiver: string) => void;
  timelineModalOpen: boolean;
  setTimelineModalOpen: (open: boolean) => void;
  infoModalOpen: boolean;
  setInfoModalOpen: (open: boolean) => void;
  deleteDialogOpen: boolean;
  setDeleteDialogOpen: (open: boolean) => void;
  confirmDelete: () => void;
  discardDialogOpen: boolean;
  setDiscardDialogOpen: (open: boolean) => void;
  confirmDiscard: () => void;
}

const LostFoundDialogs: React.FC<DialogsProps> = (props) => {
  const updateLostItem = useUpdateLostItem();
  const handleEditSave = async (values: { description: string; location: string; found_by: string; photo: string | null }) => {
    if (!props.selectedItem) return;
    const changes: any = {};
    if (values.description !== props.selectedItem.description) changes.description = values.description;
    if (values.location !== props.selectedItem.location) changes.location = values.location;
    if (values.photo !== props.selectedItem.photo) changes.photo = values.photo;
    // Only update found_by if changed, but do NOT touch history
    if (values.found_by !== props.selectedItem.found_by) changes.found_by = values.found_by;
    if (Object.keys(changes).length === 0) return; // No changes
    await updateLostItem.mutateAsync({
      id: props.selectedItem.id,
      changes,
    });
  };
  return (
    <>
      {props.selectedItem && (
        <EditLostItemModal
          open={props.editItemOpen}
          onOpenChange={props.setEditItemOpen}
          item={props.selectedItem}
          onSave={handleEditSave}
        />
      )}
      <LostItemSignatureModal
        open={props.signatureModalOpen}
        onOpenChange={props.setSignatureModalOpen}
        onConfirm={receiver => props.handleConfirmSignature(receiver)}
      />
      <LostItemTimelineModal
        open={props.timelineModalOpen}
        onOpenChange={props.setTimelineModalOpen}
        item={props.selectedItem}
      />
      <LostItemInfoModal
        open={props.infoModalOpen}
        onOpenChange={props.setInfoModalOpen}
        item={props.selectedItem}
      />
      <AlertDialog open={props.deleteDialogOpen} onOpenChange={props.setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Permanent Delete</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this item? It will be permanently erased in 48 hours unless it is restored.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={props.confirmDelete} className="bg-red-500 hover:bg-red-600">
              Confirm Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog open={props.discardDialogOpen} onOpenChange={props.setDiscardDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Discard Item</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to mark this item as discarded?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={props.confirmDiscard} className="bg-orange-500 hover:bg-orange-600">
              Discard
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default LostFoundDialogs;
